import { h } from 'vue'
import { useStateStore } from '@json-render/vue'
import { z } from 'zod'

import EventsDetailSection from '../components/EventsDetailSection.vue'

const resourceStateSchema = z.enum(['loading', 'empty', 'error', 'ready'])
const sidebarRowSchema = z.object({ key: z.string(), value: z.string() }).strict()
const agendaSpeakerSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    role: z.string(),
    imageUrl: z.string().optional(),
  })
  .strict()
const agendaItemSchema = z
  .object({
    id: z.string(),
    time: z.string(),
    meridiem: z.string(),
    endTime: z.string().optional(),
    endMeridiem: z.string().optional(),
    title: z.string(),
    meta: z.string(),
    description: z.string().optional(),
    speakerName: z.string().optional(),
    speakers: z.array(agendaSpeakerSchema).optional(),
    tag: z.string().optional(),
    isSaved: z.boolean().optional(),
    startAt: z.string().optional(),
    endAt: z.string().optional(),
    timeZone: z.string().optional(),
  })
  .strict()
const agendaDaySchema = z
  .object({
    id: z.string(),
    label: z.string(),
    headingLabel: z.string().optional(),
    sessions: z.array(agendaItemSchema),
  })
  .strict()
const personLinkSchema = z.object({ title: z.string(), url: z.string() }).strict()
const personSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    role: z.string(),
    tag: z.string().optional(),
    tags: z.array(z.string()).optional(),
    tagline: z.string().optional(),
    location: z.string().optional(),
    bio: z.string().optional(),
    imageUrl: z.string().optional(),
    isHeadliner: z.boolean().optional(),
    links: z.array(personLinkSchema).optional(),
  })
  .strict()
const attendeeSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    role: z.string().optional(),
    email: z.string(),
    lastActiveLabel: z.string().optional(),
    badge: z.string().optional(),
    imageUrl: z.string().optional(),
    portalUserId: z.string().optional(),
    contactId: z.string().optional(),
    isSelf: z.boolean().optional(),
  })
  .strict()
const exhibitorSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    booth: z.string(),
    initial: z.string(),
    accentTone: z.enum(['primary', 'success', 'warning', 'purple', 'error', 'gray', 'pink']).optional(),
    tag: z.string().optional(),
    description: z.string().optional(),
    websiteUrl: z.string().optional(),
    logoUrl: z.string().optional(),
    showAboutAndLinks: z.boolean().optional(),
    showTeamAtEvent: z.boolean().optional(),
  })
  .strict()
const exhibitorRepSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    role: z.string(),
    imageUrl: z.string().optional(),
  })
  .strict()
const exhibitorDetailSchema = z
  .object({
    state: resourceStateSchema,
    representatives: z.array(exhibitorRepSchema),
  })
  .strict()
const ticketSchema = z
  .object({
    id: z.string(),
    ticketName: z.string(),
    seatCount: z.number(),
    qrImageUrl: z.string(),
    pdfUrl: z.string().optional(),
    orderId: z.string().optional(),
    checkInStatus: z.enum(['checked-in', 'not-checked-in', 'not-applicable']).optional(),
    amountLabel: z.string().optional(),
  })
  .strict()
const eventDetailSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    imageUrl: z.string(),
    dateLabel: z.string(),
    timeLabel: z.string().optional(),
    locationLabel: z.string(),
    locationVenue: z.string().optional(),
    locationAddress: z.string().optional(),
    isLive: z.boolean().optional(),
    eventUrl: z.string().optional(),
    about: z.string(),
    organizerName: z.string(),
    organizerEmail: z.string().optional(),
    organizerPhone: z.string().optional(),
    organizerWebsite: z.string().optional(),
    organizerImageUrl: z.string().optional(),
    formatKind: z.enum(['online', 'in-person']).optional(),
    agendaSummary: z.string(),
    agendaDays: z.array(agendaDaySchema),
    speakers: z.array(personSchema),
    attendees: z.array(attendeeSchema),
    attendeesTotalCount: z.number().optional(),
    exhibitors: z.array(exhibitorSchema),
    tickets: z.array(ticketSchema),
    details: z.array(sidebarRowSchema),
    visibleSections: z
      .object({
        about: z.boolean(),
        agenda: z.boolean(),
        speakers: z.boolean(),
        attendees: z.boolean(),
        exhibitors: z.boolean(),
        tickets: z.boolean(),
      })
      .strict(),
  })
  .strict()

const speakerSessionSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    meta: z.string(),
    dayLabel: z.string().optional(),
    timeLabel: z.string().optional(),
    venue: z.string().optional(),
    description: z.string().optional(),
    tag: z.string().optional(),
    startAt: z.string().optional(),
    endAt: z.string().optional(),
    timeZone: z.string().optional(),
  })
  .strict()
const speakerDetailSchema = z
  .object({
    state: resourceStateSchema,
    speakerId: z.string(),
    bio: z.string(),
    links: z.array(personLinkSchema),
    sessions: z.array(speakerSessionSchema),
  })
  .strict()

const tabIdSchema = z.enum(['about', 'tickets', 'speakers', 'attendees', 'exhibitors'])

const detailProps = z
  .object({
    state: resourceStateSchema,
    event: eventDetailSchema.nullable().optional(),
    speakerDetail: speakerDetailSchema.optional(),
    exhibitorDetail: exhibitorDetailSchema.optional(),
    defaultTab: tabIdSchema.optional(),
    gap: z.enum(['sm', 'md', 'lg']).optional(),
    sidebarWidth: z.enum(['280', '320', '360']).optional(),
    showAbout: z.boolean().optional(),
    showAgenda: z.boolean().optional(),
    showSidebar: z.boolean().optional(),
    showSpeakers: z.boolean().optional(),
    showAttendees: z.boolean().optional(),
    showExhibitors: z.boolean().optional(),
    showTickets: z.boolean().optional(),
    isRegistered: z.boolean().optional(),
  })
  .strict()

export const EVENTS_DETAIL_EXPERIENCE_COMPONENT_DEFINITIONS = {
  EventsDetail: {
    props: detailProps,
    description: 'Overview of a single event with agenda and details card.',
  },
} as const

interface RegistryContext<Props> {
  readonly props: Props
  readonly emit: (event: string) => void
}

export const EVENTS_DETAIL_EXPERIENCE_COMPONENTS = {
  EventsDetail: ({
    props,
    emit,
  }: RegistryContext<z.infer<(typeof EVENTS_DETAIL_EXPERIENCE_COMPONENT_DEFINITIONS)['EventsDetail']['props']>>) => {
    const { set } = useStateStore()
    return h(EventsDetailSection, {
      state: props.state,
      event: props.event ?? null,
      defaultTab: props.defaultTab,
      gap: props.gap,
      sidebarWidth: props.sidebarWidth,
      showAbout: props.showAbout,
      showAgenda: props.showAgenda,
      showSidebar: props.showSidebar,
      showSpeakers: props.showSpeakers,
      showAttendees: props.showAttendees,
      showExhibitors: props.showExhibitors,
      showTickets: props.showTickets,
      isRegistered: props.isRegistered,
      speakerDetail: props.speakerDetail,
      exhibitorDetail: props.exhibitorDetail,
      onSaveSession: (sessionId: string) => {
        set('/ui/selectedAgendaSessionId', sessionId)
        emit('saveSession')
      },
      onAddToCalendar: (sessionId: string) => {
        set('/ui/selectedCalendarSessionId', sessionId)
        emit('addToCalendar')
      },
      onRegister: () => emit('register'),
      onDownloadTicket: (ticketId: string) => {
        set('/ui/selectedTicketId', ticketId)
        emit('downloadTicket')
      },
      onChat: (attendeeId: string) => {
        set('/ui/selectedAttendeeId', attendeeId)
        emit('chat')
      },
      onOpenSpeaker: (speakerId: string) => {
        set('/ui/selectedSpeakerId', speakerId)
        emit('openSpeaker')
      },
      onCloseSpeaker: () => {
        set('/ui/selectedSpeakerId', '')
        emit('closeSpeaker')
      },
      onOpenExhibitor: (exhibitorId: string) => {
        set('/ui/selectedExhibitorId', exhibitorId)
        emit('openExhibitor')
      },
      onCloseExhibitor: () => {
        set('/ui/selectedExhibitorId', '')
        emit('openExhibitor')
      },
    })
  },
} as const
